import { z } from 'zod';
import { esquemaConsultaPagina, esquemaPagina } from './comun.js';
/**
 * Contratos del catálogo público.
 *
 * El catálogo es la única superficie del sistema que se sirve a internet sin
 * sesión, así que lo que ENTRA se acota acá y lo que SALE está enumerado
 * campo por campo. No hay `passthrough` ni forma abierta: lo que no aparece en
 * `esquemaItemCatalogo` no existe para el navegador.
 *
 * Lo que deliberadamente NO está en la respuesta:
 *  · `costoNeto` del producto,
 *  · `margenOverride` del producto,
 *  · `margenDefault` de la categoría.
 *
 * En la v1 el costo no viajaba pero era derivable: la vista publicaba el
 * precio de venta y la tabla de categorías —legible incluso por un anónimo—
 * publicaba el margen, así que bastaba dividir para reconstruir el costo de
 * cada proveedor. Acá el margen no sale por ninguna vía y el precio lo calcula
 * el servidor.
 */
export const ORDENES_CATALOGO = ['relevancia', 'precio_asc', 'precio_desc', 'nombre'];
/** Largo máximo de la búsqueda. Más allá no aporta y solo da trabajo al motor. */
export const LARGO_MAXIMO_BUSQUEDA = 120;
/**
 * Mínimo de caracteres para usar el índice FULLTEXT.
 *
 * `innodb_ft_min_token_size` vale 3 por defecto en MySQL, así que un token más
 * corto NO está en el índice y `MATCH … AGAINST` devuelve cero filas: buscar
 * "3M" por fulltext no encuentra nada aunque haya cinco productos de esa
 * marca. Bajo este umbral la consulta cae a `contains`.
 */
export const MINIMO_BUSQUEDA_FULLTEXT = 3;
export const esquemaConsultaCatalogo = esquemaConsultaPagina.extend({
    /** Slug de la categoría, no su id: es lo que va en la URL compartible. */
    categoria: z
        .string()
        .trim()
        .min(1)
        .max(80)
        .regex(/^[a-z0-9-]+$/, 'El slug de categoría no es válido')
        .optional(),
    marca: z.string().trim().min(1).max(120).optional(),
    proveedorId: z.string().trim().length(36, 'El proveedor no es válido').optional(),
    q: z.string().trim().max(LARGO_MAXIMO_BUSQUEDA).optional(),
    orden: z.enum(ORDENES_CATALOGO).default('relevancia'),
    /**
     * Faena en contexto. Determina la zona de destino con la que se calcula el
     * plazo de entrega que se muestra. Solo se acepta una faena del propio
     * cliente; sin faena la zona cae a `Centro`, igual que en el carrito.
     */
    faenaId: z.string().trim().length(36, 'La faena no es válida').optional(),
});
/**
 * Un producto del catálogo.
 *
 * Los tres campos opcionales del final viajan SOLO con sesión. La identidad
 * del proveedor es la cadena de suministro y no se publica a internet: quien
 * la conoce puede saltarse a Compra Norte. La ciudad de origen sí se publica,
 * porque de ella depende el plazo que se le promete al visitante y hay cinco
 * ciudades posibles: no identifica a nadie. El stock exacto tampoco sale sin
 * sesión —es información comercial—, solo si hay o no.
 */
export const esquemaItemCatalogo = z.object({
    /** Id del ARTÍCULO, no de la oferta. Es lo único que el cliente manda al carrito. */
    id: z.string(),
    /**
     * Código propio del artículo.
     *
     * NO es el SKU del proveedor. El SKU identifica una oferta y cambiaría el día que la
     * oferta elegida cambie, así que el cliente pediría un repuesto con un código que
     * mañana significa otra cosa. El artículo tiene el suyo y es estable.
     */
    codigo: z.string(),
    /**
     * Número de parte del fabricante, cuando se conoce.
     *
     * Se publica a propósito: es lo que busca un mantenedor de faena que ya sabe qué
     * repuesto necesita, y no identifica al distribuidor —es del fabricante—.
     */
    mpn: z.string().nullable(),
    nombre: z.string(),
    descripcion: z.string(),
    marca: z.string(),
    imagenUrl: z.string().nullable(),
    categoriaId: z.string(),
    categoriaSlug: z.string(),
    categoriaNombre: z.string(),
    ciudadOrigen: z.string(),
    /** CLP neto, entero. Lo calcula el servidor; el margen no viaja. */
    precioVentaNeto: z.number().int(),
    hayStock: z.boolean(),
    pesoKg: z.number(),
    largoCm: z.number(),
    anchoCm: z.number(),
    altoCm: z.number(),
    /** Días hábiles hasta la zona de destino en contexto. */
    diasEntrega: z.number().int(),
    /**
     * Cuántas ofertas distintas hay detrás. Solo para el EQUIPO.
     *
     * Es la única señal del agrupamiento que se publica hacia adentro; el cliente no
     * recibe ni el número, porque saber que hay cuatro ofertas es saber que hay a quién
     * pedirle precio.
     */
    ofertas: z.number().int().optional(),
    stockDisponible: z.number().int().optional(),
});
export const esquemaPaginaCatalogo = esquemaPagina(esquemaItemCatalogo);
/**
 * Un documento técnico del artículo, tal como lo ve el cliente.
 *
 * Cuatro campos de la fila real NO están, y ninguno por olvido:
 *
 *  · `proveedorId`, obvio.
 *  · `productoId`, que es el id de la OFERTA: dos documentos con el mismo
 *    `productoId` dicen "estos dos vienen del mismo distribuidor", y tres
 *    fichas con tres `productoId` distintos dicen "hay tres detrás". Es el
 *    conteo de ofertas que el catálogo ya se cuida de no publicar, deducible
 *    desde otra lista.
 *  · `clave`, cuya ruta empieza por el id del proveedor.
 *  · `nombreOriginal`, que lo escribió el distribuidor y con frecuencia lleva
 *    su razón social: "Ferretería XYZ - ficha 6200.pdf". En su lugar va
 *    `etiqueta`, que la compone el servidor desde el tipo.
 *
 * `bytes` sí se publica: quien descarga desde la conectividad de una faena
 * necesita saber si son 200 KB o 12 MB antes de tocar el enlace.
 */
export const esquemaDocumentoArticulo = z.object({
    id: z.string(),
    tipo: z.enum(['datasheet', 'certificado', 'documento']),
    /** Cómo se nombra en la lista: "Ficha técnica", "Certificado", "Manual". */
    etiqueta: z.string(),
    /** Nombre con el que se descarga, compuesto desde el código del artículo. */
    nombreArchivo: z.string(),
    mime: z.string(),
    bytes: z.number().int(),
    creadoEn: z.string(),
});
export const esquemaDocumentosArticulo = z.object({
    datos: z.array(esquemaDocumentoArticulo),
    /**
     * Si el visitante puede descargar, o solo ver que existen.
     *
     * La lista es pública —saber que hay ficha técnica ayuda a decidir y no
     * cuesta ancho de banda— pero el binario exige sesión, con el mismo criterio
     * con que ya se reservan el stock exacto y el plazo de entrega. El frontend
     * lo necesita para ofrecer "entrar para descargar" en vez de un enlace que
     * responde 401 en una pestaña nueva.
     */
    descargable: z.boolean(),
});
/**
 * Categoría tal como la ve el catálogo: con su contador de productos visibles
 * para la barra lateral y SIN `margenDefault`, que es el dato con el que en la
 * v1 se despejaba el costo del proveedor.
 */
export const esquemaCategoriaPublica = z.object({
    id: z.string(),
    slug: z.string(),
    nombre: z.string(),
    /** Cuántos productos visibles tiene: activos y de proveedor aprobado. */
    cuenta: z.number().int(),
});
// ─── Asistente de búsqueda ──────────────────────────────────────────────────
/**
 * Lo que el asistente responde cuando una búsqueda no encontró lo pedido.
 *
 * NO es una respuesta generada: `alternativas` son artículos que una consulta de
 * verdad devolvió, con la misma proyección pública del catálogo. Por eso el
 * asistente no puede nombrar un producto que no vendemos, que es el defecto que
 * vuelve inservible a un asistente —sugerir lo que no existe es peor que decir
 * «no lo tengo»—.
 */
export const esquemaSugerenciaCatalogo = z.object({
    /**
     * La búsqueda que SÍ funcionó, que casi nunca es la que se escribió.
     *
     * Se publica porque es lo que hace honesta a la respuesta: la pantalla puede
     * decir «no encontré *teclado lavable de silicona*, pero esto hay en
     * *teclado*» en vez de presentar resultados de otra consulta como si fueran de
     * la que se pidió.
     */
    consultada: z.string(),
    /** Qué tan lejos quedó de lo pedido: exacta, parcial o de la misma familia. */
    cercania: z.enum(['exacta', 'parcial', 'familia']),
    alternativas: z.array(esquemaItemCatalogo),
    /**
     * Categorías donde sí hay algo parecido, para ofrecer un camino de navegación
     * cuando ninguna alternativa concreta convence.
     */
    categorias: z.array(z.object({ slug: z.string(), nombre: z.string(), cuenta: z.number().int() })),
    /** Marcas presentes entre las alternativas. Ayuda a acotar sin escribir de nuevo. */
    marcas: z.array(z.string()),
});
//# sourceMappingURL=catalogo.js.map